import { useState } from "react";
import MainLayout from "@/components/feature/MainLayout";
import { realtimeMeterData } from "@/mocks/meter";
import MeterDeepAnalysis from "./components/MeterDeepAnalysis";

export default function RealtimeMeterPage() {
  const [search, setSearch] = useState("");
  const [analysisDevice, setAnalysisDevice] = useState<{ id: string; userName: string } | null>(null);
  const filtered = realtimeMeterData.filter(d => d.userName.includes(search) || d.id.includes(search));

  return (
    <MainLayout>
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-lg font-bold text-gray-900">实时抄表监控</h2>
            <p className="text-xs text-gray-400 mt-0.5">IoT设备实时数据 · 最后更新：2026-04-10 10:16:45</p>
          </div>
          <button className="flex items-center gap-2 px-4 py-2 bg-[#E8531A] text-white text-sm rounded-lg cursor-pointer whitespace-nowrap">
            <i className="ri-refresh-line"></i>刷新数据
          </button>
        </div>

        <div className="grid grid-cols-4 gap-3">
          {[
            { label: "在线设备", value: filtered.length, color: "text-green-600", bg: "bg-green-50" },
            { label: "正常运行", value: filtered.filter(d => d.status === "正常").length, color: "text-teal-600", bg: "bg-teal-50" },
            { label: "告警设备", value: filtered.filter(d => d.alarmStatus).length, color: "text-red-600", bg: "bg-red-50" },
            { label: "待校验", value: filtered.filter(d => d.status === "待校验").length, color: "text-amber-600", bg: "bg-amber-50" },
          ].map((s, i) => (
            <div key={i} className={`${s.bg} rounded-xl p-4`}>
              <p className="text-xs text-gray-500">{s.label}</p>
              <p className={`text-2xl font-bold ${s.color}`}>{s.value}</p>
            </div>
          ))}
        </div>

        <div className="bg-white rounded-xl p-4 border border-gray-100">
          <div className="relative">
            <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm"></i>
            <input value={search} onChange={e => setSearch(e.target.value)} placeholder="搜索设备ID或用户名..." className="w-full pl-8 pr-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-[#E8531A]/50" />
          </div>
        </div>

        <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-100">
                {["设备ID","用户","设备类型","当前读数","本月用量","压力(MPa)","温度(°C)","信号","状态","更新时间","操作"].map(h => (
                  <th key={h} className="text-left text-xs text-gray-500 font-medium px-3 py-3 whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map(d => (
                <tr key={d.id} className="border-b border-gray-50 hover:bg-gray-50 transition-colors">
                  <td className="px-3 py-3 text-xs font-mono text-gray-500">{d.id}</td>
                  <td className="px-3 py-3 text-sm font-medium text-gray-800 whitespace-nowrap">{d.userName}</td>
                  <td className="px-3 py-3">
                    <span className="text-xs px-2 py-0.5 rounded-full bg-orange-50 text-[#E8531A]">{d.deviceType}</span>
                  </td>
                  <td className="px-3 py-3 text-sm font-bold text-gray-900">{d.currentReading.toLocaleString()} m³</td>
                  <td className="px-3 py-3 text-sm text-gray-700">{d.usage.toLocaleString()} m³</td>
                  <td className="px-3 py-3 text-sm text-gray-700">{d.pressure}</td>
                  <td className="px-3 py-3 text-sm text-gray-700">{d.temperature}</td>
                  <td className="px-3 py-3">
                    <div className="flex items-center gap-1">
                      <div className={`w-1.5 h-1.5 rounded-full ${d.signal > 80 ? "bg-green-500" : d.signal > 60 ? "bg-amber-500" : "bg-red-500"}`}></div>
                      <span className="text-xs text-gray-600">{d.signal}%</span>
                    </div>
                  </td>
                  <td className="px-3 py-3">
                    <span className={`text-xs px-2 py-0.5 rounded-full ${d.status === "正常" ? "bg-green-50 text-green-600" : "bg-amber-50 text-amber-600"}`}>{d.status}</span>
                    {d.alarmStatus && <span className="ml-1 text-xs px-2 py-0.5 rounded-full bg-red-50 text-red-600">{d.alarmStatus}</span>}
                  </td>
                  <td className="px-3 py-3 text-xs text-gray-400 whitespace-nowrap">{d.lastUpdate}</td>
                  <td className="px-3 py-3">
                    <button
                      onClick={() => setAnalysisDevice({ id: d.id, userName: d.userName })}
                      className="flex items-center gap-1 text-xs text-[#E8531A] hover:underline cursor-pointer whitespace-nowrap"
                    >
                      <i className="ri-bar-chart-2-line"></i>深度分析
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {analysisDevice && (
        <MeterDeepAnalysis
          userId={analysisDevice.id}
          userName={analysisDevice.userName}
          onClose={() => setAnalysisDevice(null)}
        />
      )}
    </MainLayout>
  );
}
